import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';

const TOUR_STEPS = [
  {
    target: '[data-tour="chatbot"]',
    title: 'Chat with your Data',
    desc: 'Ask any question about your claims - "What is the ICR?", "Top hospitals by amount", "Claims with TAT > 20 days". AI answers instantly from your data.',
    position: 'top-end',
  },
  {
    target: '[data-tour="nav-tabs"]',
    title: 'Section Navigation',
    desc: 'Click any tab to jump directly to that section - Portfolio, Ailments, Hospitals, Rejections, and more.',
    position: 'bottom',
  },
  {
    target: '[data-tour="export-btn"]',
    title: 'Export PPT Report',
    desc: 'Generate a branded PowerPoint report with all charts, tables, and AI insights - ready for client presentations.',
    position: 'bottom-end',
  },
  {
    target: '[data-tour="share-btn"]',
    title: 'Share Dashboard',
    desc: 'Share this dashboard via email - recipients get a live link to view the same data.',
    position: 'bottom-end',
  },
  {
    target: '[data-tour="filter-btn"]',
    fallbackTargets: ['[data-tour="filter-bar"]'],
    title: 'Smart Filters',
    desc: 'Filter by date range, claim type, status, relation, city type, employee, or sum insured. Click to expand and filters apply to all sections instantly.',
    position: 'bottom',
  },
  {
    target: '[data-tour="health-section"]',
    fallbackTargets: ['#health'],
    title: 'Policy Health at a Glance',
    desc: 'Red = needs attention, Green = healthy. Quick view of ICR, reimbursement ratio, settlement rate, TAT, and SI utilization.',
    position: 'bottom',
  },
  {
    target: '[data-tour="ai-insights"]',
    fallbackTargets: ['#ai-insights'],
    title: 'AI Observations',
    desc: 'AI analyzes your entire claims data and generates observations with actionable recommendations for each area.',
    position: 'bottom',
  },
];

const STORAGE_KEY = 'claimiq_tour_seen';

const GuidedTour = () => {
  const [step, setStep] = useState(0);
  const [show, setShow] = useState(false);
  const [rect, setRect] = useState(null);

  // Show tour only on first visit
  useEffect(() => {
    const seen = localStorage.getItem(STORAGE_KEY);
    if (!seen) {
      const timer = setTimeout(() => setShow(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const updateRect = useCallback(() => {
    if (!show) return;
    const s = TOUR_STEPS[step];
    // Try primary target, then fallback targets
    const targets = [s.target, ...(s.fallbackTargets || [])];
    let el = null;
    for (const t of targets) {
      el = document.querySelector(t);
      if (el) break;
    }
    if (el) {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      // Scroll element into view if needed
      if (r.top < 0 || r.bottom > window.innerHeight) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          const r2 = el.getBoundingClientRect();
          setRect({ top: r2.top, left: r2.left, width: r2.width, height: r2.height });
        }, 400);
      }
    } else {
      setRect(null);
    }
  }, [show, step]);

  useEffect(() => {
    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, { passive: true });
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect);
    };
  }, [updateRect]);

  const endTour = () => {
    setShow(false);
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  const next = () => {
    if (step < TOUR_STEPS.length - 1) setStep(step + 1);
    else endTour();
  };

  const prev = () => {
    if (step > 0) setStep(step - 1);
  };

  if (!show) return null;

  const current = TOUR_STEPS[step];
  const pad = 8;

  // Calculate tooltip position with viewport bounds check
  let tooltipStyle = {};
  const tooltipH = 180; // approx tooltip height
  const tooltipW = 320;
  if (rect) {
    let pos = current.position || 'bottom';
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    // Auto-flip: if tooltip would go below viewport, show above
    if ((pos === 'bottom' || pos === 'bottom-end') && rect.top + rect.height + pad + 12 + tooltipH > vh) {
      pos = pos === 'bottom' ? 'top' : 'top-end';
    }
    // Auto-flip: if tooltip would go above viewport, show below
    if ((pos === 'top' || pos === 'top-end') && rect.top - tooltipH - pad < 0) {
      pos = pos === 'top' ? 'bottom' : 'bottom-end';
    }
    if (pos === 'bottom') {
      tooltipStyle = { top: rect.top + rect.height + pad + 12, left: Math.min(Math.max(16, rect.left + rect.width / 2 - 160), vw - tooltipW - 16) };
    } else if (pos === 'bottom-end') {
      tooltipStyle = { top: rect.top + rect.height + pad + 12, left: Math.min(Math.max(16, rect.left + rect.width - tooltipW), vw - tooltipW - 16) };
    } else if (pos === 'top-end') {
      tooltipStyle = { top: Math.max(16, rect.top - tooltipH - pad), left: Math.min(Math.max(16, rect.left + rect.width - tooltipW), vw - tooltipW - 16) };
    } else if (pos === 'top') {
      tooltipStyle = { top: Math.max(16, rect.top - tooltipH - pad), left: Math.min(Math.max(16, rect.left + rect.width / 2 - 160), vw - tooltipW - 16) };
    }
  }

  return (
    <div className="fixed inset-0 z-[9999] print:hidden" style={{pointerEvents: 'auto'}}>
      {/* Overlay with cutout */}
      <svg className="absolute inset-0 w-full h-full" style={{pointerEvents: 'none'}}>
        <defs>
          <mask id="tour-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left - pad} y={rect.top - pad}
                width={rect.width + pad * 2} height={rect.height + pad * 2}
                rx="8" fill="black"
              />
            )}
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.5)" mask="url(#tour-mask)" />
      </svg>

      {/* Highlight border around target */}
      {rect && (
        <div
          className="absolute border-2 border-purple-400 rounded-lg pointer-events-none"
          style={{
            top: rect.top - pad, left: rect.left - pad,
            width: rect.width + pad * 2, height: rect.height + pad * 2,
            boxShadow: '0 0 0 4px rgba(139, 92, 246, 0.2)',
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        className="absolute bg-white rounded-xl shadow-2xl border border-gray-200 p-5 w-[320px]"
        style={{ ...tooltipStyle, zIndex: 10000 }}
      >
        <button onClick={endTour} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
          <X size={14} />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={14} className="text-purple-500" />
          <h3 className="text-sm font-bold text-gray-800">{current.title}</h3>
        </div>
        <p className="text-xs text-gray-600 leading-relaxed mb-4">{current.desc}</p>

        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            {TOUR_STEPS.map((_, i) => (
              <div key={i} className={`h-1.5 rounded-full transition-all ${i === step ? 'w-4 bg-purple-500' : 'w-1.5 bg-gray-200'}`} />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button onClick={endTour} className="text-[10px] text-gray-400 hover:text-gray-600">
              Skip tour
            </button>
            {step > 0 && (
              <button onClick={prev} className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors">
                <ChevronLeft size={14} />
              </button>
            )}
            <button
              onClick={next}
              className="px-3 py-1.5 text-[11px] font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 flex items-center gap-1 transition-colors"
            >
              {step === TOUR_STEPS.length - 1 ? 'Done' : 'Next'}
              {step < TOUR_STEPS.length - 1 && <ChevronRight size={12} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidedTour;
